import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    asyncScheduler,
    catchError,
    map,
    Observable,
    observeOn,
    of,
    switchMap,
    tap,
    throwError
} from 'rxjs';
import { Draft } from '../apimodels/draft/draft';
import { MotorsDraft } from '../apimodels/draft/motors.draft';
import { ListingResponse } from '../apimodels/draft/listing-response';
import { SaveDraft } from '../apimodels/draft/save-draft';
import { SaveDraftResponse } from '../apimodels/draft/save-draft-response';
import { UpdateCurrentStep } from '../apimodels/draft/update-current-step';
import { ValidateListingResponse } from '../apimodels/draft/validate-listing-response';
import { CategoryEnum } from '../enums/category';
import { Environment, ENVIRONMENT } from '@classifieds-sell/environment';
import { WindowRef } from '@trademe/tangram';
import { NEW_RELIC_SERVICE_TOKEN } from 'libs/trademe-core-ui/src/lib/new-relic/new-relic-token';
import { INewRelicService } from 'libs/trademe-core-ui/src/lib/new-relic/new-relic-service.interface';
import { getDraftStorageKey, getTimestampStorageKey, isTimestampExpired } from '../apimodels/draft/draft-utils';

const PING_REDIRECT_PARAM = 'psd';

export type DraftType = Draft | MotorsDraft;

@Injectable({
    providedIn: 'root'
})
export class DraftService {
    constructor(
        private httpClient: HttpClient,
        @Inject(ENVIRONMENT) private env: Environment,
        private windowRef: WindowRef,
        @Inject(NEW_RELIC_SERVICE_TOKEN)
        private _newRelicService: INewRelicService
    ) { }

    getListingDraft<T extends DraftType>(
        listingId: number,
        categoryId: CategoryEnum,
        subCategory?: string
    ): Observable<T> {
        if (!listingId || listingId === 0) {
            // If listingId is not valid, directly fetch from API
            // this will be the case for create
            return this.fetchDraftFromApi<T>(
                listingId,
                categoryId,
                subCategory
            );
        }

        const draftStorageKey = getDraftStorageKey(
            categoryId,
            listingId,
            subCategory
        );
        const timestampStorageKey = getTimestampStorageKey(
            categoryId,
            listingId,
            subCategory
        );

        return this.getStoredDraft<T>(draftStorageKey, timestampStorageKey).pipe(
            switchMap((draft) => {
                if (draft) {
                    return of(draft);
                }

                return this.fetchDraftFromApi<T>(
                    listingId,
                    categoryId,
                    subCategory,
                    draftStorageKey,
                    timestampStorageKey
                );
            })
        );
    }

    getStoredDraft<T extends DraftType>(
        draftStorageKey: string,
        timestampStorageKey: string
    ): Observable<T | null> {
        const storedDraft = localStorage.getItem(draftStorageKey);
        const storedTimestamp = localStorage.getItem(timestampStorageKey);

        if (!storedDraft && this.isPaymentRedirect()) {
            this._newRelicService.noticeError(
                'Draft was not stored before the payment redirect',
                false
            );
        }

        if (storedDraft && storedTimestamp) {
            const timestamp = parseInt(storedTimestamp, 10);

            if (!isTimestampExpired(timestamp)) {
                try {
                    const parsedDraft: T = JSON.parse(storedDraft);
                    return of({...parsedDraft, draftLoadedFromLocalStorage: true}).pipe(observeOn(asyncScheduler));
                } catch (error) {
                    this.handleDraftRetrievalError(
                        error,
                        draftStorageKey,
                        timestampStorageKey
                    );
                }
            } else {
                localStorage.removeItem(draftStorageKey);
                localStorage.removeItem(timestampStorageKey);
            }
        }
        return of(null);
    }


    fetchDraftFromApi<T extends DraftType>(
        listingId: number,
        categoryId: CategoryEnum,
        subCategory: string | undefined,
        draftStorageKey?: string,
        timestampStorageKey?: string
    ): Observable<T> {
        let apiUrl = `${this.env.apiUrl}/Draft/Category/${categoryId}`;
        if (subCategory) {
            apiUrl += `/${subCategory}`;
        }
        apiUrl += `/Listing/${listingId}`;


        return this.httpClient
            .get<T>(
                apiUrl,
                { withCredentials: true }
            )
            .pipe(
                map((draft) => {
                    if(draftStorageKey) {
                        localStorage.setItem(
                            draftStorageKey,
                            JSON.stringify(draft)
                        );
                    }

                    if(timestampStorageKey) {
                        localStorage.setItem(
                            timestampStorageKey,
                            Date.now().toString()
                        );
                    }
                    return draft;
                }),
                catchError((error) => {
                    return throwError(() => error);
                })
            );
    }

    private handleDraftRetrievalError(
        error: any,
        draftStorageKey: string,
        timestampStorageKey: string
    ): void {
        localStorage.removeItem(draftStorageKey);
        localStorage.removeItem(timestampStorageKey);

        if (!this.isPaymentRedirect()) {
            return;
        }

        const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
        const errorStack =
            error instanceof Error
                ? error.stack
                : 'No stack trace available';
        const errorDetails = `Message: ${errorMessage}\nStacktrace: ${errorStack}`;

        this._newRelicService.noticeError(
            `Failed to retrieve draft after payment redirect: ${errorDetails}`,
            false
        );
    }

    isPaymentRedirect(): boolean {
        return this.windowRef.useWindow((w) => {
            const queryStringParams = new URLSearchParams(w.location.search);
            return queryStringParams.has(PING_REDIRECT_PARAM);
        });
    }

    getIncompleteDraft<T extends DraftType>(
        categoryId: CategoryEnum,
        subCategory?: string
    ): Observable<T> {
        let apiUrl = `${this.env.apiUrl}/Draft/Category/${categoryId}`;
        if (subCategory) {
            apiUrl += `/${subCategory}`;
        }
        return this.httpClient.get<T>(
            apiUrl,
            { withCredentials: true }
        );
    }

    saveDraft(draft: SaveDraft): Observable<SaveDraftResponse> {
        return this.httpClient.post<SaveDraftResponse>(
            `${this.env.apiUrl}/Draft/Save`,
            draft,
            { withCredentials: true }
        );
    }

    createListing(
        categoryId: CategoryEnum,
        subCategory?: string
    ): Observable<ListingResponse> {
        let subCategoryPath = '';
        if (subCategory) {
            subCategoryPath = `/${subCategory}`;
        }
        return this.httpClient.post<ListingResponse>(
            `${this.env.apiUrl}/Draft/CreateListing/${categoryId}${subCategoryPath}`,
            null,
            { withCredentials: true }
        );
    }

    editListing<T extends DraftType>(
        listingId: number,
        categoryId: CategoryEnum,
        draft: T,
        listingEndDate: Date,
        subCategory?: string
    ): Observable<ListingResponse> {
        const draftStorageKey = getDraftStorageKey(
            categoryId,
            listingId,
            subCategory
        );
        const timestampStorageKey = getTimestampStorageKey(
            categoryId,
            listingId,
            subCategory
        );

        let apiUrl = `${this.env.apiUrl}/Draft/EditListing/${categoryId}`;
        if (subCategory) {
            apiUrl += `/${subCategory}`;
        }
        apiUrl += `/Listing/${listingId}`;

        return this.httpClient.post<ListingResponse>(
            apiUrl,
            {
                categoryId: categoryId,
                draftData: JSON.stringify(draft),
                listingEndDate: listingEndDate,
                subCategory: subCategory
            },
            { withCredentials: true }
        ).pipe(
            tap(() => {
                // Remove draft from local storage on successful API call
                localStorage.removeItem(draftStorageKey);
                localStorage.removeItem(timestampStorageKey);
            })
        );
    }

    relistListing<T extends DraftType>(
        listingId: number,
        categoryId: CategoryEnum,
        draft: T,
        subCategory?: string
    ): Observable<ListingResponse> {
        const draftStorageKey = getDraftStorageKey(
            categoryId,
            listingId,
            subCategory
        );
        const timestampStorageKey = getTimestampStorageKey(
            categoryId,
            listingId,
            subCategory
        );
        let apiUrl = `${this.env.apiUrl}/Draft/RelistListing/${categoryId}`;
        if (subCategory) {
            apiUrl += `/${subCategory}`;
        }
        apiUrl += `/Listing/${listingId}`;
        return this.httpClient.post<ListingResponse>(
            apiUrl,
            {
                categoryId: categoryId,
                draftData: JSON.stringify(draft),
                subCategory: subCategory // subCategory is passed here
            },
            { withCredentials: true }
        ).pipe(
            tap(() => {
                // Remove draft from local storage on successful API call
                localStorage.removeItem(draftStorageKey);
                localStorage.removeItem(timestampStorageKey);
            })
        );
    }

    validateDraftListing(): Observable<ValidateListingResponse> {
        return this.httpClient.post<ValidateListingResponse>(
            // TODO: this is obviously not used, remove
            `${this.env.apiUrl}/Draft/ValidateListing/${CategoryEnum.residentialForRent}`,
            null,
            { withCredentials: true }
        );
    }

    updateCurrentStep(currentStep: UpdateCurrentStep): Observable<boolean> {
        return this.httpClient.post<boolean>(
            `${this.env.apiUrl}/Draft/UpdateCurrentStep`,
            currentStep,
            { withCredentials: true }
        );
    }
}